import { captureError } from "@/common/helpers/sentryHelper";
import { useMessage } from "@/common/messages";
import { ServerEvent } from "@/common/notification";
import { useWebchatCallRequestStore } from "@/common/stores/nonpersistingStores/webchatCallRequest";
import { notificationListener } from "@/hooks/notification";
import {
	WebchatVideoCallRequestChangedDto,
	WebchatVideoCallRequestStatus,
} from "@/repository/models/webchatCallRequest";
import { startWebchatVideoOutbound } from "@/repository/queryFns/call";
import { useIntl } from "react-intl";

/**
 * Headless listener that triggers the agent-initiated webchat video call once the
 * customer accepts. The agent's TeamsCallAgent cannot dial an ACS communication user,
 * so the call is placed server-side: this POST makes the bot ring the agent from the
 * configured resource account and then invite the customer. The agent answers the
 * incoming ring like any other Unify call.
 */
export const WebchatVideoCallDialer = () => {
	const intl = useIntl();
	const { show: showMessage } = useMessage();
	const markAccepted = useWebchatCallRequestStore(
		(state) => state.markAccepted
	);

	notificationListener(
		ServerEvent.WebchatVideoCallRequestChanged,
		async (data: WebchatVideoCallRequestChangedDto) => {
			if (data.status !== WebchatVideoCallRequestStatus.Accepted) {
				return;
			}

			// keep the request tracked (accepted) so the Start button stays blocked while the bot
			// rings the agent; it clears once the call goes active or the request expires.
			const trackedConversationId = markAccepted(data.requestId);

			if (!trackedConversationId) {
				return; // request was not initiated from this client
			}

			try {
				await startWebchatVideoOutbound(data.conversationId, data.requestId);
			} catch (err: any) {
				captureError(err, "Error starting webchat video call", undefined, {
					conversationId: data.conversationId,
					requestId: data.requestId,
				});
				showMessage("workspace", {
					intent: "error",
					text: intl.formatMessage({
						id: "Interaction.Messages.WebchatCallStartFailed",
					}),
				});
			}
		}
	);

	return null;
};
